import { Check, X, Sparkles } from 'lucide-react'
import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge'
import { Button } from '#app/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '#app/components/ui/card'
import { cn } from '#app/utils/misc'
import { type Route } from './+types/pricing'

export const meta: Route.MetaFunction = () => [
  { title: 'Prijzen - Woonplan' },
  { name: 'description', content: 'Transparante prijzen voor elke fase van je renovatie' }
]

const plans = [
  {
    name: 'Starter',
    description: 'Perfect voor kleine renovaties',
    price: '0',
    currency: '€',
    period: 'Altijd gratis',
    popular: false,
    features: [
      { text: '1 actief project', included: true },
      { text: 'Tot 50 foto\'s', included: true },
      { text: 'Basis planning tools', included: true },
      { text: 'Mood boards', included: true },
      { text: '2 teamleden', included: true },
      { text: 'Community support', included: true },
      { text: 'Geavanceerde rapportages', included: false },
      { text: 'API toegang', included: false },
      { text: 'Prioriteit support', included: false },
    ],
    cta: 'Start Gratis',
    ctaLink: '/signup',
  },
  {
    name: 'Professional',
    description: 'Voor serieuze renovatieprojecten',
    price: '19',
    currency: '€',
    period: 'per maand',
    popular: true,
    features: [
      { text: 'Onbeperkte projecten', included: true },
      { text: 'Onbeperkte foto\'s & video\'s', included: true },
      { text: 'Geavanceerde planning', included: true },
      { text: 'Budget tracking', included: true },
      { text: '10 teamleden per project', included: true },
      { text: 'Email support', included: true },
      { text: 'Geavanceerde rapportages', included: true },
      { text: 'Integraties', included: true },
      { text: 'Prioriteit support', included: false },
    ],
    cta: 'Start 14 dagen gratis',
    ctaLink: '/signup?plan=professional',
  },
  {
    name: 'Business',
    description: 'Voor aannemers en professionals',
    price: '49',
    currency: '€',
    period: 'per maand',
    popular: false,
    features: [
      { text: 'Alles van Professional', included: true },
      { text: 'Onbeperkte teamleden', included: true },
      { text: 'White-label opties', included: true },
      { text: 'Klanten dashboard', included: true },
      { text: 'Factuur integratie', included: true },
      { text: 'Custom workflows', included: true },
      { text: 'API toegang', included: true },
      { text: 'Dedicated support', included: true },
      { text: 'Training & onboarding', included: true },
    ],
    cta: 'Contact Sales',
    ctaLink: '/contact?plan=business',
  },
]

const faqs = [
  {
    question: 'Kan ik op elk moment upgraden of downgraden?',
    answer: 'Ja, je kunt op elk moment je abonnement aanpassen. Bij een upgrade betaal je het verschil, bij een downgrade krijg je krediet voor de volgende periode.',
  },
  {
    question: 'Wat gebeurt er met mijn data als ik stop?',
    answer: 'Je behoudt altijd toegang tot je data. Na opzegging kun je nog 30 dagen inloggen om je projecten te exporteren.',
  },
  {
    question: 'Zijn er opstartkosten of verborgen kosten?',
    answer: 'Nee, onze prijzen zijn volledig transparant. Je betaalt alleen het maandelijkse bedrag, geen opstartkosten of extra fees.',
  },
  {
    question: 'Kan ik eerst uitproberen voordat ik betaal?',
    answer: 'Natuurlijk! Start met ons gratis Starter plan of probeer Professional 14 dagen gratis uit.',
  },
  {
    question: 'Bieden jullie kortingen voor non-profits?',
    answer: 'Ja, we bieden 50% korting voor geregistreerde non-profit organisaties. Neem contact op voor meer informatie.',
  },
  {
    question: 'Hoe werkt de team functionaliteit?',
    answer: 'Je kunt teamleden uitnodigen per project. Zij krijgen toegang tot dat specifieke project en kunnen bijdragen volgens hun rol.',
  },
]

export default function PricingPage() {
  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="container max-w-6xl mx-auto px-4 text-center mb-16">
        <Badge className="mb-4" variant="secondary">
          <Sparkles className="h-3 w-3 mr-1" />
          Transparante prijzen
        </Badge>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Kies het plan dat bij je past
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Van hobbyproject tot professionele renovatie. 
          Start gratis en groei in je eigen tempo.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="container max-w-7xl mx-auto px-4 mb-24">
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card 
              key={plan.name}
              className={cn(
                "relative",
                plan.popular && "border-primary shadow-lg scale-105"
              )}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="px-3 py-1">
                    Meest gekozen
                  </Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription className="text-base">
                  {plan.description}
                </CardDescription>
                <div className="mt-4">
                  <span className="text-4xl font-bold">{plan.currency}{plan.price}</span>
                  <span className="text-muted-foreground ml-2">{plan.period}</span>
                </div>
              </CardHeader>
              
              <CardContent>
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      {feature.included ? (
                        <Check className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                      ) : (
                        <X className="h-5 w-5 text-muted-foreground/50 shrink-0 mt-0.5" />
                      )}
                      <span className={cn(
                        "text-sm",
                        !feature.included && "text-muted-foreground/50"
                      )}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant={plan.popular ? "default" : "outline"}
                  asChild
                >
                  <Link to={plan.ctaLink}>
                    {plan.cta}
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      </section>

      {/* Enterprise Section */}
      <section className="container max-w-4xl mx-auto px-4 mb-24">
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4">Enterprise</h2>
                <p className="text-muted-foreground mb-6">
                  Grootschalige renovatieprojecten vereisen speciale aandacht. 
                  We maken graag een oplossing op maat voor jouw organisatie.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span>Onbeperkte projecten & gebruikers</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span>SLA & dedicated support</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span>On-premise installatie mogelijk</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-primary" />
                    <span>Custom integraties & API</span>
                  </li>
                </ul>
              </div>
              <div className="text-center md:text-right">
                <p className="text-2xl font-bold mb-4">Prijs op aanvraag</p>
                <Button size="lg" asChild>
                  <Link to="/contact?plan=enterprise">
                    Vraag demo aan
                  </Link>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQs */}
      <section className="container max-w-4xl mx-auto px-4">
        <h2 className="text-3xl font-bold text-center mb-12">
          Veelgestelde vragen
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          {faqs.map((faq, index) => (
            <div key={index}>
              <h3 className="font-semibold mb-2">{faq.question}</h3>
              <p className="text-muted-foreground">{faq.answer}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container max-w-4xl mx-auto px-4 mt-24 text-center">
        <Card className="p-8 md:p-12">
          <h2 className="text-3xl font-bold mb-4">
            Klaar om te beginnen?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Start vandaag nog gratis en ontdek hoe Woonplan je renovatie makkelijker maakt.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to="/start">
                Start gratis
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">
                Contact sales
              </Link>
            </Button>
          </div>
        </Card>
      </section>
    </div>
  )
}